/* eslint-disable replo/no-tailwind-literal-color */
import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { DeploymentStatusTag } from "@v2/features/page-renderer/components/DeploymentStatusTag";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ChevronDown, FileText } from "lucide-react";

import PageSelectionDropdown from "./PageSelectionDropdown";

type PageNameWithDropdownProps = {
  page: FileWithLatestFileVersion;
  projectId: string;
  isPageSelectionPopoverOpen: boolean;
  setIsPageSelectionPopoverOpen: (open: boolean) => void;
  onPageSelect: (page: FileWithLatestFileVersion) => void;
};

export default function PageNameWithDropdown({
  page,
  projectId,
  isPageSelectionPopoverOpen,
  setIsPageSelectionPopoverOpen,
  onPageSelect,
}: PageNameWithDropdownProps) {
  const logEvent = useLogAnalytics();
  const [isMobileDockOpen, setIsMobileDockOpen] = React.useState(false);

  return (
    <div className="flex items-center gap-2">
      <div className="hidden md:flex flex-row">
        <Popover
          onOpenChange={setIsPageSelectionPopoverOpen}
          showCloseButton={false}
          align="start"
          side="bottom"
          sideOffset={4}
          triggerAsChild
          trigger={
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                logEvent("editor.nav.pages.click");
              }}
              endEnhancer={<ChevronDown />}
              UNSAFE_className={twMerge(
                "shadow-button-selectable",
                isPageSelectionPopoverOpen && "bg-surface",
              )}
            >
              <div className="flex flex-row items-center gap-1.5 mr-3">
                <Tooltip content={page.name} triggerAsChild>
                  <div className="max-w-[150px] truncate">{page.name}</div>
                </Tooltip>
                <DeploymentStatusTag file={page} size="sm" />
              </div>
            </Button>
          }
          UNSAFE_className="w-[35vw] max-h-[75vh] overflow-y-auto pb-0 pr-0"
        >
          <PageSelectionDropdown
            projectId={projectId}
            handlePageSelectionCardClick={onPageSelect}
            currentPageId={page.id}
          />
        </Popover>
      </div>

      <div className="md:hidden flex flex-row">
        <Drawer
          open={isMobileDockOpen}
          onOpenChange={setIsMobileDockOpen}
          trigger={
            <Button
              variant="secondary"
              size="base"
              endEnhancer={<ChevronDown />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMobileDockOpen(true);
              }}
            >
              <FileText />
            </Button>
          }
        >
          <PageSelectionDropdown
            projectId={projectId}
            handlePageSelectionCardClick={onPageSelect}
            currentPageId={page.id}
          />
        </Drawer>
      </div>
    </div>
  );
}
